import { useApi } from '@/api'
import { useSpaClientStore } from '@/stores/spaClient'
import {
    MessageTransporter,
    MqttTransporterStrategy,
    TransporterConnectionStatus,
    TransporterEvent,
    VesselClient,
    VesselIdentityEvent,
    VesselStateEvent,
    RebootCommand,
    RequestWaterlabSample,
    PresetEventType,
    type DeviceType,
    type MqttSession,
    type PresetsDocument
} from '@geckoal/vessel-client'
import type { TransporterEventCallback } from '@geckoal/vessel-client/src/lib/messageHandler/transporterEventListener'
import {
    GatewayConnectionStatus,
    OperationMode,
    VesselConnectionStatus,
    FlowZoneInitiator,
    type IdentityShadowV1,
    type StaticSpaConfigV1,
    type StaticStateVesselV1
} from '@geckoal/web-models'
import {
    GeckoIotClient,
    MqttTransporter,
    EventChannel,
    ZoneType,
    ZoneCapability,
    FlowZoneType,
    CommandKind,
    CommandVia,
    type FlowZone as IotFlowZone,
    type LightingZone as IotLightingZone,
    type TemperatureControlZone as IotTemperatureControlZone,
    type DeviceComponent,
    type ServiceCommand,
    // The package root re-exports the config-core SpaMetadata as `SpaMetadata`; the
    // DeviceIdentity's spa metadata is exported under `DeviceSpaMetadata`.
    type DeviceSpaMetadata as SpaMetadata
} from '@geckoal/gecko-iot-client-js'
import { ref, computed, type Ref } from 'vue'

/**
 * Unified spa-connection composable shared by the admin portal's spa views.
 *
 * It wraps BOTH client implementations behind a single reactive interface so the
 * views (GeckoSpaStatus, VesselSchedulesCalendar, SpaStateValidator) share one
 * connection lifecycle and template binding regardless of which client is active:
 *
 *   - 'iot'    -> @geckoal/gecko-iot-client-js GeckoIotClient (the new default)
 *   - 'vessel' -> @geckoal/vessel-client VesselClient (the legacy client)
 *
 * The active client is chosen from the spaClient Pinia store, which only affects the
 * admin portal. The vessel path reproduces the previous behavior exactly; the iot
 * path maps the iot client's zone/connectivity/identity models onto the same
 * StaticStateVesselV1-shaped `spaState` / `spaConfig` the templates already consume.
 *
 * ## Capabilities not available on the iot client path
 * The iot client does NOT currently expose the following, so the composable gates
 * them with the `supports*` flags below and the views hide/disable the matching UI
 * rather than calling (and throwing) on an unsupported capability:
 *   - RF channel range / set channel          (`supportsRf`)
 *   - operation mode + preset schedules        (`supportsOperationMode`, `supportsPresets`)
 *   - timezone set                             (`supportsTimezone`)
 * Do not invent iot-client APIs for these; they are intentionally no-ops on the iot path.
 *
 * Maintenance commands (`supportsCommands`) ARE wired on the iot path: reboot /
 * request-water-sample dispatch through GeckoIotClient.sendCommand using the
 * ServiceCommands a component advertises. Only TOPIC (Eco 1) commands are
 * dispatchable here; EXECUTION (Eco 2) commands are gated — see the IOT COMMAND
 * DISPATCH section below.
 */

export interface SpaClientConnectParams {
    /** Monitor id (device id) to connect to. */
    monitorId: string
    /** Owning account id, required to fetch the spa configuration. Optional for monitor-only routes. */
    accountId?: number
}

const FLOW_ZONE_PREFIX = 'flow'
const LIGHTING_ZONE_PREFIX = 'lighting'
const TEMPERATURE_ZONE_PREFIX = 'temperatureControl'

/**
 * A plain reactive snapshot of the iot client's rich {@link DeviceIdentity}.
 *
 * We copy the values out of the DeviceIdentity getters (rather than storing the
 * class instance) so Vue reactivity re-renders on each IDENTITY_UPDATE, and so
 * the views bind to a stable, serializable shape. This is kept SEPARATE from the
 * vessel-shaped `vesselIdentity`; the iot Identity view in GeckoSpaStatus renders
 * from this, while the legacy vessel table keeps reading `vesselIdentity`.
 */
export interface IotIdentityView {
    shadowVersion: string | null
    spaConfigId: string | null
    spa: SpaMetadata
    components: readonly DeviceComponent[]
    isLoaded: boolean
}

export function useSpaClient() {
    const store = useSpaClientStore()

    // --- Shared reactive interface consumed by the views ---
    const spaState = ref<StaticStateVesselV1 | undefined>()
    const spaConfig = ref<StaticSpaConfigV1 | undefined>()
    const vesselIdentity = ref<IdentityShadowV1>()
    /** Rich iot-client identity snapshot; undefined on the vessel path or before load. */
    const iotIdentity = ref<IotIdentityView | undefined>()
    const presets = ref<PresetsDocument | null>(null)
    const presetsCompatible = ref(false)
    const cloudConnectionStatus = ref<TransporterConnectionStatus>(TransporterConnectionStatus.UNKNOWN)
    const snackBarText = ref<string | undefined>()
    const lastKnownInitiators = ref<Record<string, FlowZoneInitiator[]>>({})
    const isInConnection = ref(false)

    /** Which client is active for this connection instance. Captured at connect time. */
    const activeClientType = ref(store.clientType)
    const isIot = computed(() => activeClientType.value === 'iot')

    // --- Capability flags (false on iot where the API is not exposed) ---
    const supportsRf = computed(() => !isIot.value)
    const supportsOperationMode = computed(() => !isIot.value)
    const supportsPresets = computed(() => !isIot.value)
    const supportsTimezone = computed(() => !isIot.value)

    // ============================================================
    // IOT COMMAND DISPATCH
    // ============================================================
    //
    // The iot client dispatches maintenance commands (reboot / request water
    // sample) via GeckoIotClient.sendCommand(command: ServiceCommand). A command
    // is sourced from the DeviceComponent.commands getter on the loaded identity.
    //
    // There are two command transports (see @geckoal/gecko-iot-client-js):
    //   - CommandVia.TOPIC     (Eco 1) — published to a device topic; NO executor
    //                                     required, dispatchable here.
    //   - CommandVia.EXECUTION (Eco 2) — an AWS IoT command execution; sendCommand
    //                                     THROWS unless a `commandExecutor` was
    //                                     passed to the GeckoIotClient constructor.
    //
    // connectIot() does NOT supply a commandExecutor and there is no backend / AWS
    // plumbing available in the admin portal, so EXECUTION commands are treated as
    // non-dispatchable: we never fabricate a CommandExecutor, never call sendCommand
    // with an EXECUTION command (which would throw), and exclude them from
    // supportsCommands. Only TOPIC commands are dispatched.

    /** Vessel DeviceType enum value -> iot component deviceType token. */
    const DEVICE_TYPE_TO_IOT_TOKEN: Record<number, string> = {
        0: 'en', // DeviceType.IN_TOUCH_3_EN
        1: 'co', // DeviceType.IN_TOUCH_3_CO
        2: 'waterlab' // DeviceType.WATERLAB
    }

    /** All ServiceCommands advertised by the currently loaded iot identity's components. */
    const advertisedCommands = computed<readonly ServiceCommand[]>(() => {
        const components = iotIdentity.value?.components
        if (!components) return []
        return components.flatMap((component) => component.commands)
    })

    /** TOPIC (dispatchable, executor-free) ServiceCommands advertised by the identity. */
    const dispatchableCommands = computed<readonly ServiceCommand[]>(() =>
        advertisedCommands.value.filter((command) => command.via === CommandVia.TOPIC)
    )

    /**
     * Find a dispatchable (TOPIC) ServiceCommand of the given kind, optionally
     * restricted to the component matching an iot deviceType token. EXECUTION-only
     * commands yield undefined (non-dispatchable in this environment).
     */
    function findDispatchableCommand(kind: CommandKind, iotDeviceType?: string): ServiceCommand | undefined {
        const components = iotIdentity.value?.components
        if (!components) return undefined
        for (const component of components) {
            if (iotDeviceType !== undefined && component.deviceType !== iotDeviceType) continue
            const match = component.commands.find((command) => command.kind === kind && command.via === CommandVia.TOPIC)
            if (match) return match
        }
        return undefined
    }

    // --- Capability gate for maintenance commands ---
    // Vessel path: always supported (legacy behavior). Iot path: supported only once
    // the loaded identity advertises at least one dispatchable TOPIC command. Recomputes
    // reactively because it reads `iotIdentity` (repopulated on every IDENTITY_UPDATE).
    const supportsCommands = computed(() => {
        if (!isIot.value) return true
        return dispatchableCommands.value.length > 0
    })

    // --- Underlying clients (only one is active at a time) ---
    let vesselClient: VesselClient | undefined
    let iotClient: GeckoIotClient | undefined
    let iotUnsubscribers: Array<() => void> = []

    // === Shared connection status computeds (both paths drive `spaState`) ===
    const gatewayStatus = computed(() => spaState.value?.connectivity_?.gatewayStatus)
    const vesselStatus = computed(() => spaState.value?.connectivity_?.vesselStatus)
    const connected = computed(() => gatewayStatus.value === GatewayConnectionStatus.CONNECTED)
    const spaConnected = computed(() => vesselStatus.value === VesselConnectionStatus.RUNNING)

    const cloudConnectionStatusText = computed(() => {
        if (cloudConnectionStatus.value === TransporterConnectionStatus.CONNECTED) return 'Connected'
        if (cloudConnectionStatus.value === TransporterConnectionStatus.DISCONNECTED) return 'Disconnected'
        return 'Not Connected'
    })

    // === Config / state fetch (identical for both paths) ===
    const dictionaryToObjectWithId = (dictionary: Record<string, unknown>) => {
        return Object.entries(dictionary).reduce((acc, [key, value]) => {
            return { ...acc, [key]: { ...(value as Record<string, unknown>), configId: key } }
        }, {})
    }

    const fixConfigIdFromConfig = (config: StaticSpaConfigV1): StaticSpaConfigV1 => {
        const newConfig = { ...config }
        if (newConfig.zones?.flow) {
            newConfig.zones.flow = dictionaryToObjectWithId(newConfig.zones.flow)
        }
        if (newConfig.zones?.temperatureControl) {
            newConfig.zones.temperatureControl = dictionaryToObjectWithId(newConfig.zones.temperatureControl)
        }
        if (newConfig.zones?.lighting) {
            newConfig.zones.lighting = dictionaryToObjectWithId(newConfig.zones.lighting)
        }
        return newConfig
    }

    async function fetchSpaConfiguration(accountId: number, monitorId: string): Promise<StaticSpaConfigV1> {
        const config = (await useApi().getAccountMonitorConfigurationV1({
            accountId,
            monitorId
        })) as unknown as StaticSpaConfigV1
        return fixConfigIdFromConfig(config)
    }

    async function fetchLiveStreamSession(monitorId: string) {
        return await useApi().getMonitorLiveStreamV2({ monitorId })
    }

    const getErrorEventDetails = (eventName: string) => {
        switch (eventName) {
            case 'ECONNREFUSED':
                return 'Connection Refused'
            case 'ECONNRESET':
                return 'Connection Reset'
            case 'EADDRINUSE':
                return 'Address In Use'
            case 'ENOTFOUND':
                return 'Not Found'
            default:
                return 'Unknown Error'
        }
    }

    // ============================================================
    // VESSEL PATH — reproduces the previous per-view behavior 1:1
    // ============================================================

    const onVesselSpaStateUpdate = (newSpaState: StaticStateVesselV1) => {
        const flowZones = newSpaState.zones?.flow
        if (flowZones) {
            for (const [zoneId, zone] of Object.entries(flowZones)) {
                if (zone?.initiators_ !== undefined) {
                    lastKnownInitiators.value[zoneId] = zone.initiators_ as FlowZoneInitiator[]
                }
            }
        }
        spaState.value = newSpaState
    }

    const onCloudStatusUpdate = (event: TransporterEvent) => {
        if (event === TransporterEvent.CONNECTED) {
            cloudConnectionStatus.value = TransporterConnectionStatus.CONNECTED
            snackBarText.value = undefined
        } else if (event === TransporterEvent.DISCONNECTED) {
            cloudConnectionStatus.value = TransporterConnectionStatus.DISCONNECTED
        } else {
            cloudConnectionStatus.value = TransporterConnectionStatus.UNKNOWN
        }
    }

    const onSocketError = (reason: string) => {
        snackBarText.value = getErrorEventDetails(reason)
    }

    async function connectVessel(params: SpaClientConnectParams, options: { loadPresets: boolean; onExpiredSession?: () => void }) {
        const { monitorId, accountId } = params

        const liveStreamSession = await fetchLiveStreamSession(monitorId)
        const mqttSession: MqttSession = {
            ...liveStreamSession,
            url: liveStreamSession.brokerUrl
        }
        const mqttStrategy = new MqttTransporterStrategy(mqttSession, { reconnectPeriod: 1000 * 30 })
        const transporter = new MessageTransporter(mqttStrategy)

        if (accountId !== undefined) {
            spaConfig.value = await fetchSpaConfiguration(accountId, monitorId)
        }

        vesselClient = new VesselClient(transporter, spaConfig.value ?? ({} as StaticSpaConfigV1))

        vesselClient.onUpdate(TransporterEvent.ERROR, onSocketError)
        vesselClient.onUpdate(TransporterEvent.CONNECTED, onCloudStatusUpdate as TransporterEventCallback)
        vesselClient.onUpdate(TransporterEvent.DISCONNECTED, onCloudStatusUpdate as TransporterEventCallback)
        if (options.onExpiredSession) {
            vesselClient.onUpdate(TransporterEvent.EXPIRED_SESSION, options.onExpiredSession)
        }
        vesselClient.onUpdate(VesselStateEvent.STATE_UPDATE, () => {
            onVesselSpaStateUpdate(vesselClient?.getVesselState() as StaticStateVesselV1)
            vesselIdentity.value = vesselClient?.getIdentity() as IdentityShadowV1
        })
        vesselClient.onUpdate(VesselIdentityEvent.IDENTITY_UPDATE, () => {
            vesselIdentity.value = vesselClient?.getIdentity() as IdentityShadowV1
        })

        await vesselClient.connect()

        if (options.loadPresets && vesselClient.presetManager) {
            await vesselClient.presetManager.on(PresetEventType.LOADED, (presetsDoc) => {
                presets.value = presetsDoc
                presetsCompatible.value = vesselClient?.presetManager?.isCompatible ?? false
            })
            await vesselClient.presetManager.on(PresetEventType.UPDATED, (presetsDoc) => {
                presets.value = presetsDoc
            })
            await vesselClient.presetManager.on(PresetEventType.DOCUMENT_UPDATED, (presetsDoc) => {
                presets.value = presetsDoc
            })
            await vesselClient.presetManager.init()
            presetsCompatible.value = vesselClient.presetManager.isCompatible
        } else if (options.loadPresets) {
            presetsCompatible.value = false
        }
    }

    async function disconnectVessel() {
        if (vesselClient?.presetManager) {
            try {
                await vesselClient.presetManager.clearAllListeners()
            } catch {
                /* ignore */
            }
        }
        try {
            await vesselClient?.disconnect()
        } catch {
            /* ignore */
        }
        vesselClient?.clearListeners()
        vesselClient = undefined
    }

    // ============================================================
    // IOT PATH — maps GeckoIotClient models onto the vessel shape
    // ============================================================

    /** Build a StaticStateVesselV1-shaped snapshot and a matching spaConfig from the iot zones. */
    function syncStateFromIot() {
        if (!iotClient) return

        // The views read merged config+state objects keyed by `configId`. On the vessel
        // path the library merges config + live state into a single object (carrying `id`,
        // `minSpeed`, `setPoint`, etc.). We reproduce that merged shape here so the same
        // template bindings work unchanged on the iot path.
        const flowState: Record<string, Record<string, unknown>> = {}
        const flowConfig: Record<string, Record<string, unknown>> = {}
        const lightingState: Record<string, Record<string, unknown>> = {}
        const lightingConfig: Record<string, Record<string, unknown>> = {}
        const tempState: Record<string, Record<string, unknown>> = {}
        const tempConfig: Record<string, Record<string, unknown>> = {}

        const flowZones = iotClient.getZonesByType(ZoneType.FLOW_ZONE) as readonly IotFlowZone[]
        for (const zone of flowZones) {
            const id = `${FLOW_ZONE_PREFIX}:${zone.id}`
            const initiators = (zone.initiators ?? undefined) as FlowZoneInitiator[] | undefined
            if (initiators !== undefined) lastKnownInitiators.value[id] = initiators
            const speedConfig = zone.speedConfig
            flowState[id] = {
                id,
                active: zone.active ?? undefined,
                speed: zone.speed ?? undefined,
                initiators_: initiators,
                minSpeed: speedConfig?.minimum ?? 0,
                maxSpeed: speedConfig?.maximum ?? 0,
                incrementSpeed: speedConfig?.stepIncrement ?? 0
            }
            flowConfig[id] = {
                configId: id,
                // Mark waterfalls so the view can split them out the same way it does for vessel.
                waterfalls: zone.type === FlowZoneType.WATERFALL_ZONE ? [id] : []
            }
        }

        const lightingZones = iotClient.getZonesByType(ZoneType.LIGHTING_ZONE) as readonly IotLightingZone[]
        for (const zone of lightingZones) {
            const id = `${LIGHTING_ZONE_PREFIX}:${zone.id}`
            const rgbi = zone.rgbi
            lightingState[id] = {
                id,
                active: zone.active ?? undefined,
                rgbi: rgbi ? [rgbi.r, rgbi.g, rgbi.b, rgbi.i ?? 0] : undefined,
                effect: zone.effect ?? undefined
            }
            lightingConfig[id] = { configId: id }
        }

        const tempZones = iotClient.getZonesByType(ZoneType.TEMPERATURE_CONTROL_ZONE) as readonly IotTemperatureControlZone[]
        for (const zone of tempZones) {
            const id = `${TEMPERATURE_ZONE_PREFIX}:${zone.id}`
            tempState[id] = {
                id,
                status_: zone.status ?? undefined,
                temperature_: zone.temperature ?? undefined,
                mode_: zone.mode ? { eco: zone.mode.eco } : undefined,
                setPoint: zone.targetTemperature ?? undefined,
                minTemperatureSetPointC: zone.minTemperatureSetPoint ?? 0,
                maxTemperatureSetPointC: zone.maxTemperatureSetPoint ?? 0
            }
            tempConfig[id] = { configId: id }
        }

        const connectivity = iotClient.connectivityStatus

        spaConfig.value = {
            zones: {
                flow: flowConfig,
                lighting: lightingConfig,
                temperatureControl: tempConfig
            }
        } as unknown as StaticSpaConfigV1

        spaState.value = {
            connectivity_: {
                gatewayStatus: (connectivity?.gatewayStatus as GatewayConnectionStatus) ?? GatewayConnectionStatus.UNKNOWN,
                vesselStatus: (connectivity?.vesselStatus as VesselConnectionStatus) ?? VesselConnectionStatus.UNKNOWN
            },
            zones: {
                flow: flowState,
                lighting: lightingState,
                temperatureControl: tempState
            },
            // Operation mode / rf / timezone / waterlab are not exposed by the iot client.
            features: {}
        } as unknown as StaticStateVesselV1
    }

    function syncIdentityFromIot() {
        if (!iotClient) return
        // GeckoIotClient.identity ALWAYS returns a DeviceIdentity (never undefined).
        const identity = iotClient.identity

        // Keep the thin vessel-shaped mapping so existing config-id fallbacks keep working.
        vesselIdentity.value = {
            spaConfigId_: identity.spaConfigId ?? undefined
        } as unknown as IdentityShadowV1

        if (!identity.isLoaded) {
            iotIdentity.value = undefined
            return
        }

        // Surface the full identity as a plain snapshot so Vue re-renders on each update.
        iotIdentity.value = {
            shadowVersion: identity.shadowVersion,
            spaConfigId: identity.spaConfigId,
            spa: identity.spa,
            components: identity.components,
            isLoaded: identity.isLoaded
        }
    }

    async function connectIot(params: SpaClientConnectParams) {
        const { monitorId, accountId } = params

        const liveStreamSession = await fetchLiveStreamSession(monitorId)

        // Fetch the vessel-shaped config too so any config-derived view fallbacks keep working.
        if (accountId !== undefined) {
            try {
                spaConfig.value = await fetchSpaConfiguration(accountId, monitorId)
            } catch {
                /* non-fatal — the iot client loads its own config */
            }
        }

        const transporter = new MqttTransporter({ brokerUrl: liveStreamSession.brokerUrl, monitorId })
        iotClient = new GeckoIotClient({ deviceId: monitorId, transporter })

        iotUnsubscribers.push(
            iotClient.on(EventChannel.CONNECTIVITY_UPDATE, (status) => {
                if (status.transportConnected) {
                    cloudConnectionStatus.value = TransporterConnectionStatus.CONNECTED
                    snackBarText.value = undefined
                } else {
                    cloudConnectionStatus.value = TransporterConnectionStatus.DISCONNECTED
                }
                syncStateFromIot()
            })
        )
        iotUnsubscribers.push(iotClient.on(EventChannel.ZONE_UPDATE, () => syncStateFromIot()))
        iotUnsubscribers.push(iotClient.on(EventChannel.STATE_UPDATE, () => syncStateFromIot()))
        iotUnsubscribers.push(iotClient.on(EventChannel.IDENTITY_UPDATE, () => syncIdentityFromIot()))

        await iotClient.connect()
        syncStateFromIot()
        syncIdentityFromIot()

        // No preset schedules on the iot client.
        presets.value = null
        presetsCompatible.value = false
    }

    async function disconnectIot() {
        for (const unsubscribe of iotUnsubscribers) {
            try {
                unsubscribe()
            } catch {
                /* ignore */
            }
        }
        iotUnsubscribers = []
        try {
            await iotClient?.disconnect()
        } catch {
            /* ignore */
        }
        iotClient = undefined
    }

    // Find an iot zone by the synthetic composite id used in `spaState`/`spaConfig`.
    function findIotFlowZone(compositeId: string): IotFlowZone | undefined {
        const rawId = compositeId.startsWith(`${FLOW_ZONE_PREFIX}:`) ? compositeId.slice(FLOW_ZONE_PREFIX.length + 1) : compositeId
        return (iotClient?.getZonesByType(ZoneType.FLOW_ZONE) as readonly IotFlowZone[] | undefined)?.find((z) => z.id === rawId)
    }

    function findIotLightingZone(compositeId: string): IotLightingZone | undefined {
        const rawId = compositeId.startsWith(`${LIGHTING_ZONE_PREFIX}:`) ? compositeId.slice(LIGHTING_ZONE_PREFIX.length + 1) : compositeId
        return (iotClient?.getZonesByType(ZoneType.LIGHTING_ZONE) as readonly IotLightingZone[] | undefined)?.find((z) => z.id === rawId)
    }

    function findIotTemperatureZone(compositeId: string): IotTemperatureControlZone | undefined {
        const rawId = compositeId.startsWith(`${TEMPERATURE_ZONE_PREFIX}:`) ? compositeId.slice(TEMPERATURE_ZONE_PREFIX.length + 1) : compositeId
        return (iotClient?.getZonesByType(ZoneType.TEMPERATURE_CONTROL_ZONE) as readonly IotTemperatureControlZone[] | undefined)?.find((z) => z.id === rawId)
    }

    // ============================================================
    // Public unified connection lifecycle + controls
    // ============================================================

    async function connect(params: SpaClientConnectParams, options: { loadPresets?: boolean; onExpiredSession?: () => void } = {}) {
        // Latch the client type for the lifetime of this connection.
        activeClientType.value = store.clientType
        isInConnection.value = true
        try {
            if (isIot.value) {
                await connectIot(params)
            } else {
                await connectVessel(params, { loadPresets: options.loadPresets ?? false, onExpiredSession: options.onExpiredSession })
            }
        } finally {
            isInConnection.value = false
        }
    }

    async function disconnect() {
        if (isIot.value) {
            await disconnectIot()
        } else {
            await disconnectVessel()
        }
        spaState.value = undefined
        spaConfig.value = undefined
        vesselIdentity.value = undefined
        iotIdentity.value = undefined
        presets.value = null
        presetsCompatible.value = false
        lastKnownInitiators.value = {}
    }

    function isConnected(): boolean {
        if (isIot.value) return iotClient?.isConnected ?? false
        return vesselClient?.isConnected() ?? false
    }

    // --- Controls (dispatch to the active client; no-op/guard where unsupported) ---

    async function setTemperatureSetPoint(zoneId: string, temperatureC: number) {
        if (isIot.value) {
            findIotTemperatureZone(zoneId)?.setTargetTemperature(temperatureC)
            return
        }
        await vesselClient?.setTemperatureControlZoneSetPoint(zoneId, temperatureC)
    }

    async function setFlowZoneState(zoneId: string, state: { active?: boolean; speed?: number }) {
        if (isIot.value) {
            const zone = findIotFlowZone(zoneId)
            if (!zone) return
            if (state.active === false) {
                try {
                    zone.deactivate()
                } catch {
                    /* a non-user-demand initiator is active — leave it running */
                }
            } else if (state.speed !== undefined && zone.hasCapability(ZoneCapability.SET_SPEED)) {
                zone.setSpeed(state.speed, state.active ?? true)
            } else {
                zone.activate()
            }
            return
        }
        await vesselClient?.setFlowZoneState(zoneId, state)
    }

    async function setFlowZoneActivationStatus(zoneId: string, active: boolean) {
        if (isIot.value) {
            const zone = findIotFlowZone(zoneId)
            if (!zone) return
            if (active) zone.activate()
            else {
                try {
                    zone.deactivate()
                } catch {
                    /* ignore when a non-user-demand initiator keeps it active */
                }
            }
            return
        }
        await vesselClient?.setFlowZoneActivationStatus(zoneId, active)
    }

    async function setLightingZoneActivationStatus(zoneId: string, active: boolean) {
        if (isIot.value) {
            const zone = findIotLightingZone(zoneId)
            if (!zone) return
            if (active) zone.activate()
            else zone.deactivate()
            return
        }
        await vesselClient?.setLightingZoneActivationStatus(zoneId, active)
    }

    async function setOperationMode(mode: OperationMode) {
        if (isIot.value) return // not supported on the iot client
        await vesselClient?.setOperationMode(mode)
    }

    async function setTimezone(timezone: { id: string; offset: number }) {
        if (isIot.value) return // not supported on the iot client
        await vesselClient?.setTimezone(timezone)
    }

    async function setRfChannel(channel: number) {
        if (isIot.value) return // not supported on the iot client
        await vesselClient?.setChannelRF(channel)
    }

    function getRfChannelRange(): { min: number; max: number } | undefined {
        if (isIot.value) return undefined // not supported on the iot client
        return vesselClient?.getRFChannelRange()
    }

    /** Dispatch a TOPIC ServiceCommand through the iot client, surfacing failures. */
    async function dispatchIotCommand(command: ServiceCommand | undefined) {
        // No dispatchable command (unavailable on this device, identity not loaded,
        // or only an EXECUTION command exists) — safe no-op, matching how the view
        // disables unsupported actions.
        if (!command || !iotClient) return
        try {
            await iotClient.sendCommand(command)
        } catch (error) {
            snackBarText.value = error instanceof Error ? error.message : 'Failed to send command'
        }
    }

    function sendRebootCommand(deviceType: DeviceType) {
        if (isIot.value) {
            // Map the vessel DeviceType to the matching iot component and dispatch its
            // advertised TOPIC reboot command. Unavailable / EXECUTION-only -> no-op.
            const iotDeviceType = DEVICE_TYPE_TO_IOT_TOKEN[deviceType as unknown as number]
            void dispatchIotCommand(findDispatchableCommand(CommandKind.REBOOT, iotDeviceType))
            return
        }
        void vesselClient?.sendCommand(new RebootCommand({ deviceType }))
    }

    function requestWaterlabSample() {
        if (isIot.value) {
            // Dispatch the advertised TOPIC water-sample command. Unavailable -> no-op.
            void dispatchIotCommand(findDispatchableCommand(CommandKind.REQUEST_WATER_SAMPLE))
            return
        }
        void vesselClient?.sendCommand(new RequestWaterlabSample())
    }

    return {
        // identity of the active client
        clientType: activeClientType as Readonly<Ref<'iot' | 'vessel'>>,
        isIot,

        // reactive state consumed by the views
        spaState,
        spaConfig,
        vesselIdentity,
        iotIdentity,
        presets,
        presetsCompatible,
        cloudConnectionStatus,
        cloudConnectionStatusText,
        snackBarText,
        lastKnownInitiators,
        isInConnection,

        // derived connection status
        gatewayStatus,
        vesselStatus,
        connected,
        spaConnected,

        // capability gates
        supportsRf,
        supportsOperationMode,
        supportsPresets,
        supportsTimezone,
        supportsCommands,

        // lifecycle
        connect,
        disconnect,
        isConnected,

        // controls
        setTemperatureSetPoint,
        setFlowZoneState,
        setFlowZoneActivationStatus,
        setLightingZoneActivationStatus,
        setOperationMode,
        setTimezone,
        setRfChannel,
        getRfChannelRange,
        sendRebootCommand,
        requestWaterlabSample
    }
}
